import api from './api';

export interface WeeklySlot {
  id?: number;
  weekday: number;
  start_time: string;
  end_time: string;
  appointment_type: 'online' | 'physical' | 'both';
  max_bookings: number;
  is_active: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WeeklySlotCreate {
  weekday: number;
  start_time: string;
  end_time: string;
  appointment_type: 'online' | 'physical' | 'both';
  max_bookings: number;
  is_active: boolean;
  notes?: string;
}

export interface BulkUpdateRequest {
  slots: WeeklySlotCreate[];
}

export interface GeneratePeriodRequest {
  start_date: string;
  end_date: string;
}

export const WEEKDAYS = [
  { value: 0, label: 'Lundi' },
  { value: 1, label: 'Mardi' },
  { value: 2, label: 'Mercredi' },
  { value: 3, label: 'Jeudi' },
  { value: 4, label: 'Vendredi' },
  { value: 5, label: 'Samedi' },
  { value: 6, label: 'Dimanche' },
];

export const APPOINTMENT_TYPES = [
  { value: 'online', label: 'En ligne' },
  { value: 'physical', label: 'Physique' },
  { value: 'both', label: 'Les deux' },
];

class WeeklySlotApiService {
  /**
   * Récupère tous les créneaux hebdomadaires
   */
  async getWeeklySlots(): Promise<WeeklySlot[]> {
    try {
      console.log('🔄 Appel API: /api/weekly-slots/');
      const response = await api.get('/api/weekly-slots/');
      console.log('✅ Réponse API créneaux:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur API créneaux:', error);
      console.error('📊 Détails erreur:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des créneaux');
    }
  }

  /**
   * Crée un nouveau créneau hebdomadaire
   */
  async createWeeklySlot(slotData: WeeklySlotCreate): Promise<WeeklySlot> {
    try {
      const response = await api.post('/api/weekly-slots/create/', slotData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création du créneau:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la création du créneau');
    }
  }

  /**
   * Met à jour un créneau hebdomadaire existant
   */
  async updateWeeklySlot(slotId: number, slotData: Partial<WeeklySlotCreate>): Promise<WeeklySlot> {
    try {
      const response = await api.put(`/api/weekly-slots/${slotId}/update/`, slotData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du créneau:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour du créneau');
    }
  }

  /**
   * Supprime un créneau hebdomadaire
   */
  async deleteWeeklySlot(slotId: number): Promise<void> {
    try {
      await api.delete(`/api/weekly-slots/${slotId}/delete/`);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du créneau:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la suppression du créneau');
    }
  }

  /**
   * Met à jour en masse tous les créneaux hebdomadaires
   */
  async bulkUpdateWeeklySlots(slots: WeeklySlotCreate[]): Promise<WeeklySlot[]> {
    try {
      const response = await api.post('/api/weekly-slots/bulk-update/', { slots });
      return response.data.slots;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour en masse:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour en masse');
    }
  }

  /**
   * Génère les créneaux pour une période donnée
   */
  async generateSlotsForPeriod(startDate: string, endDate: string): Promise<{ message: string; created_count: number }> {
    try {
      const response = await api.post('/api/weekly-slots/generate-period/', {
        start_date: startDate,
        end_date: endDate
      });
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la génération des créneaux:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la génération des créneaux');
    }
  }

  /**
   * Valide les données d'un créneau
   */
  validateSlot(slot: WeeklySlotCreate): string[] {
    const errors: string[] = [];

    if (slot.weekday < 0 || slot.weekday > 6) {
      errors.push('Le jour de la semaine doit être entre 0 (Lundi) et 6 (Dimanche)');
    }

    if (!slot.start_time || !slot.end_time) {
      errors.push('Les heures de début et de fin sont requises');
    }

    if (slot.start_time && slot.end_time && slot.start_time >= slot.end_time) {
      errors.push('L\'heure de fin doit être après l\'heure de début');
    }

    if (slot.max_bookings < 1) {
      errors.push('Le nombre maximum de réservations doit être au moins 1');
    }

    if (!['online', 'physical', 'both'].includes(slot.appointment_type)) {
      errors.push('Le type de rendez-vous doit être "online", "physical" ou "both"');
    }

    return errors;
  }

  /**
   * Formate l'heure pour l'affichage
   */
  formatTime(time: string): string {
    return time.substring(0, 5); // Retourne HH:MM
  }

  /**
   * Génère des créneaux horaires pour une journée (10h-22h, créneaux d'1h)
   */
  generateDefaultSlotsForDay(weekday: number): WeeklySlotCreate[] {
    const slots: WeeklySlotCreate[] = [];
    for (let hour = 10; hour < 22; hour++) {
      const start = `${String(hour).padStart(2, '0')}:00`;
      const end = `${String(hour + 1).padStart(2, '0')}:00`;
      slots.push({
        weekday,
        start_time: start,
        end_time: end,
        appointment_type: 'both',
        max_bookings: 1,
        is_active: true,
        notes: `Créneau ${start}-${end}`
      });
    }
    return slots;
  }

  /**
   * Génère l'agenda complet Lundi–Dimanche de 10h à 22h
   */
  generateFullWeekAgenda(): WeeklySlotCreate[] {
    const slots: WeeklySlotCreate[] = [];
    for (let weekday = 0; weekday <= 6; weekday++) {
      slots.push(...this.generateDefaultSlotsForDay(weekday));
    }
    return slots;
  }

  /**
   * Groupe les créneaux par jour de la semaine
   */
  groupSlotsByWeekday(slots: WeeklySlot[]): Record<number, WeeklySlot[]> {
    return slots.reduce((groups, slot) => {
      if (!groups[slot.weekday]) {
        groups[slot.weekday] = [];
      }
      groups[slot.weekday].push(slot);
      return groups;
    }, {} as Record<number, WeeklySlot[]>);
  }
}

export const weeklySlotApiService = new WeeklySlotApiService();
